import { AlertTriangle, Check, RefreshCw, X } from 'lucide-react'
import { useState } from 'react'
import {
  useApproveWithdrawal,
  useRejectWithdrawal,
  useWithdrawalQueue,
} from '@/hooks/useOps'
import type { PendingWithdrawal } from '@/lib/api'
import { formatAmount, shortAddress } from '@/lib/format'
import { useSession } from '@/lib/session'
import { Button } from '@/ui/Button'
import { CardBody, CardHeader, Housing } from '@/ui/Card'
import {
  Badge,
  Callout,
  CopyButton,
  EmptyState,
  ErrorState,
  Modal,
  Skeleton,
} from '@/ui/primitives'

/** bCDX is an 18-decimal token. Named rather than inlined at four call sites. */
const BCDX_DECIMALS = 18

type Pending = { kind: 'approve' | 'reject'; row: PendingWithdrawal }

/**
 * The withdrawal approval queue.
 *
 * ── Why this screen exists at all ──
 * The payout executor only ever pays rows in `approved`, and every path a user can
 * reach writes `requested`. So the entire user-facing API — every endpoint, every DTO,
 * every session check — cannot move money on its own; something with admin rights has
 * to say yes first. This table is that "yes". It is the one control that still holds
 * if the request path has a bug in it, which is why both actions are behind an explicit
 * confirmation rather than a single click in a dense table.
 */
export function Withdrawals() {
  const { isAdmin } = useSession()
  const { data, isLoading, isError, error, refetch, isFetching } = useWithdrawalQueue(isAdmin)
  const [pending, setPending] = useState<Pending | null>(null)

  const approve = useApproveWithdrawal()
  const reject = useRejectWithdrawal()

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-80 w-full" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <Housing>
        <ErrorState
          message={(error as Error | null)?.message ?? 'Could not load the withdrawal queue.'}
          retry={() => void refetch()}
        />
      </Housing>
    )
  }

  const mismatches = data.withdrawals.filter((w) => w.destinationMismatch)

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="display display-tight text-3xl">Withdrawals</h1>
          <p className="mt-1.5 text-sm text-muted">
            {data.count === 0
              ? 'Nothing awaiting a decision.'
              : `${data.count} awaiting approval or payout.`}
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void refetch()}
          loading={isFetching}
          iconLeft={<RefreshCw className="size-3.5" aria-hidden />}
        >
          Refresh
        </Button>
      </header>

      {mismatches.length > 0 && (
        <Callout tone="danger" title="Destination mismatch">
          {mismatches.length === 1 ? 'One row pays' : `${mismatches.length} rows pay`} to an
          address other than the requesting wallet. There is no destination parameter anywhere
          in the request path, so the API cannot produce this — find out what wrote{' '}
          {mismatches.length === 1 ? 'it' : 'them'} before approving.
        </Callout>
      )}

      <Housing>
        <CardHeader
          title="Queue"
          subtitle="Requested rows need a decision. Approved rows are already released to the payout executor and are shown so a stuck payout is visible."
        />
        <CardBody className="pt-4">
          {data.withdrawals.length === 0 ? (
            <EmptyState
              title="Queue is empty"
              description="Requests appear here the moment a user submits one."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-line">
                    <th className="label py-2 text-left">Wallet</th>
                    <th className="label py-2 text-right">Gross bCDX</th>
                    <th className="label py-2 text-left">Status</th>
                    <th className="label py-2 text-left">Requested</th>
                    <th className="label py-2 text-right">Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {data.withdrawals.map((row) => (
                    <tr key={row.id} className="border-b border-line last:border-0">
                      <td className="py-3 pr-4 align-middle">
                        <span className="flex items-center gap-1">
                          <span className="tnum text-ink">{shortAddress(row.address)}</span>
                          <CopyButton value={row.address} label="Copy wallet address" />
                        </span>
                        {row.destinationMismatch && (
                          <span className="mt-1 flex items-center gap-1 text-[10px] text-fault-text">
                            <AlertTriangle className="size-3" aria-hidden />
                            pays to {shortAddress(row.to)}
                          </span>
                        )}
                      </td>
                      <td className="tnum py-3 pr-4 text-right align-middle text-ink">
                        {formatAmount(row.amount, BCDX_DECIMALS)}
                      </td>
                      <td className="py-3 pr-4 align-middle">
                        <Badge tone={row.status === 'approved' ? 'accent' : 'warning'}>
                          {row.status}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 align-middle text-muted">
                        {formatWhen(row.requestedAt)}
                      </td>
                      <td className="py-3 text-right align-middle">
                        {row.status === 'requested' ? (
                          <span className="inline-flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => setPending({ kind: 'approve', row })}
                              iconLeft={<Check className="size-3.5" aria-hidden />}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setPending({ kind: 'reject', row })}
                              iconLeft={<X className="size-3.5" aria-hidden />}
                            >
                              Reject
                            </Button>
                          </span>
                        ) : (
                          // Already released. There is no un-approve, so offering one
                          // here would be a button that cannot do what it says.
                          <span className="text-xs text-faint">Released to payout</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Housing>

      <DecisionModal
        pending={pending}
        onClose={() => {
          setPending(null)
          approve.reset()
          reject.reset()
        }}
        onApprove={(id) => approve.mutateAsync(id).then(() => setPending(null))}
        onReject={(id, reason) =>
          reject.mutateAsync({ id, reason }).then(() => setPending(null))
        }
        busy={approve.isPending || reject.isPending}
        error={(approve.error ?? reject.error) as Error | null}
      />
    </div>
  )
}

/**
 * One modal for both decisions.
 *
 * Shared because the confirmation it presents is the same shape — who, how much, and
 * what is about to happen — and two near-identical dialogs drift. What differs is the
 * reason field, which reject requires and approve has no use for.
 */
function DecisionModal({
  pending,
  onClose,
  onApprove,
  onReject,
  busy,
  error,
}: {
  pending: Pending | null
  onClose: () => void
  onApprove: (id: string) => Promise<unknown>
  onReject: (id: string, reason: string) => Promise<unknown>
  busy: boolean
  error: Error | null
}) {
  const [reason, setReason] = useState('')

  const row = pending?.row
  const isReject = pending?.kind === 'reject'
  // The server enforces 3–500 characters. Mirrored here so the operator finds out
  // before the round trip, not after it.
  const reasonValid = reason.trim().length >= 3 && reason.trim().length <= 500

  return (
    <Modal
      open={pending !== null}
      onClose={() => {
        setReason('')
        onClose()
      }}
      title={isReject ? 'Reject withdrawal' : 'Approve withdrawal'}
      footer={
        row && (
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setReason('')
                onClose()
              }}
            >
              Cancel
            </Button>
            {isReject ? (
              <Button
                variant="danger"
                loading={busy}
                disabled={!reasonValid}
                onClick={() => void onReject(row.id, reason.trim()).then(() => setReason(''))}
              >
                Reject
              </Button>
            ) : (
              <Button loading={busy} onClick={() => void onApprove(row.id)}>
                Approve payout
              </Button>
            )}
          </div>
        )
      }
    >
      {row && (
        <div className="space-y-4">
          <div className="surface-glass-2 divide-y divide-line overflow-hidden rounded-[10px] ring-1 ring-line">
            <Field label="Wallet" value={row.address} mono />
            <Field label="Destination" value={row.to} mono warn={row.destinationMismatch} />
            <Field label="Gross bCDX" value={formatAmount(row.amount, BCDX_DECIMALS)} mono />
          </div>

          {row.destinationMismatch && (
            <Callout tone="danger" title="Destination mismatch">
              This pays somewhere other than the requesting wallet. The API has no parameter
              that can produce this.
            </Callout>
          )}

          {isReject ? (
            <label className="block">
              <span className="label">Reason (shown to the user)</span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Why this request is being declined."
                className="surface-glass-2 mt-2 w-full resize-y rounded-[10px] px-3 py-2 text-sm text-ink ring-1 ring-line outline-none placeholder:text-faint focus:ring-accent-edge"
              />
              <span className="mt-1.5 block text-[10px] text-faint">
                Required, 3–500 characters. A rejection with no reason is a support ticket by
                construction — the user's only next step is to ask why.
              </span>
            </label>
          ) : (
            <Callout tone="warning" title="What this does">
              Approving releases the row to the payout executor. It does not send funds
              itself — settlement is a separate step — and there is no un-approve.
            </Callout>
          )}

          {error && (
            <Callout tone="danger" title="Request failed">
              {error.message}
            </Callout>
          )}
        </div>
      )}
    </Modal>
  )
}

function Field({
  label,
  value,
  mono,
  warn,
}: {
  label: string
  value: string
  mono?: boolean
  warn?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-4 py-2.5">
      <span className="label">{label}</span>
      <span
        className={`min-w-0 truncate text-right text-sm ${mono ? 'tnum' : ''} ${
          warn ? 'text-fault-text' : 'text-ink'
        }`}
      >
        {value}
      </span>
    </div>
  )
}

/** Coarse relative time. A ticking second hand on a queue nobody watches is noise. */
function formatWhen(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 90) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}
